import React, { useState, useEffect, useMemo } from 'react';
import {
    Grid,
    TextField,
    Typography,
    InputAdornment,
    FormHelperText,
} from '@mui/material';
import { trakrefBlue, Grey2 } from '../../../color_utils';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import PropTypes from 'prop-types';
import UserInfo from "../../../UserInfo";

export const WeightInput = ({
    header,
    columnSpan,
    variant,
    value,
    onChange,
    removePadding,
    lbsError,
    customHelperText,
    required,
    disabled,
    showConversionArrows,
    headerColor,
    uselabel,
    headerSx,
    showHelperTextContainer,
    singleLbsField,
    headerAsLabel
}) => {
    const [weight, setWeight] = useState(0);
    const [lbs, setLbs] = useState(0);
    const [ozs, setOzs] = useState(0);
    const [displayLbs, setDisplayLbs] = useState(0);
    const [displayOzs, setDisplayOzs] = useState(0);
    const [displayKgs, setDisplayKgs] = useState(0)
    const [displayOnlyLbs, setDisplayOnlyLbs] = useState(0);
    const headerStyle = { marginTop: '1%' };
    const inputStyle = removePadding ? { paddingTop: '0px' } : {};

    // There's a case where weightUnits is empty. so we make it default to use lbs fields
    const userWeightPreferenceLbs = UserInfo.weightUnits === "Lbs" || !UserInfo.weightUnits;
    const userWeightPreferenceKgs = UserInfo.weightUnits === "Kgs";
    // When singleLbsField is true, render a single decimal-lbs input (no oz "2nd field"),
    // regardless of the user's onlyLbsDecimal preference. Kgs preference is unaffected.
    const useSingleLbs = singleLbsField || UserInfo.onlyLbsDecimal === true;
    if (removePadding) {
        headerStyle.paddingTop = '0px';
    }

    const isValidWeightValue = (value) => !isNaN(value) && value > 0;

    useEffect(() => {
        if (UserInfo.weightUnits === 'Lbs' && !useSingleLbs) {
            if (isValidWeightValue(value)) {
                setWeight(value);
                let integer = Math.trunc(value);
                let decimal = Number((value - integer).toFixed(5));
                setLbs(integer);
                setDisplayLbs(integer);
                let ounce = decimal / 0.0625;
                setOzs(ounce);
                setDisplayOzs(ounce);
            }
            else {
                setDisplayLbs(0);
                setDisplayOzs(0);
                setWeight(0);
            }
        } else if (UserInfo.weightUnits === 'Lbs' && useSingleLbs) {
            if (isValidWeightValue(value)) {
                setWeight(value);
                setDisplayOnlyLbs(value);
            }
            else {
                setWeight(0);
                setDisplayOnlyLbs(0);
            }
        }
        else if (UserInfo.weightUnits === "Kgs") {
            if (isValidWeightValue(value)) {
                let Kgs = value / 2.20462;

                if (Kgs % 1 === 0) {
                    setDisplayKgs(Kgs)
                } else {
                    setDisplayKgs(Number(Kgs.toFixed(2)))
                }
                setWeight(value);
            }
            else {
                setWeight(0);
                setDisplayKgs(0);
            }
        }
    }, []);

    useEffect(() => {
        onChange(weight < 0 && !isNaN(weight) ? 0 : weight);
    }, [weight]);

    const setPounds = (newPounds) => {
        let newValue = parseFloat(newPounds);
        if (!isValidWeightValue(newValue)) {
            setLbs(0);
            setWeight(0)
            return setDisplayLbs(0);
        }
        if (!isNaN(newValue)) { // if newValue is valid
            setLbs(newValue);
            setDisplayLbs(newValue);
            setWeight(newValue + parseFloat((0.0625 * ozs)));
        } else {
            setDisplayLbs(0);
            if (ozs != null) {
                setLbs(0);
                setWeight(newValue + parseFloat((0.0625 * ozs)));
            } else {
                setLbs(0);
                setWeight(0);
            }
        }
    };

    const setOunces = (newOz) => {
        let newValue = parseFloat(newOz);
        if (!isValidWeightValue(newOz)) {
            setOzs(0);
            setWeight(parseFloat(lbs));
            return setDisplayOzs(0);
        }
        if (!isNaN(newValue)) {
            setOzs(newValue);
            setWeight(parseFloat(lbs) + parseFloat((0.0625 * newValue)));
        } else {
            setDisplayOzs(null);
            if (lbs != null) {
                setOzs(0);
                setWeight(parseFloat(lbs));
            } else {
                setOzs(null);
                setWeight(null);
            }
        }
    };

    const setKilo = (kilo) => {
        if (!isValidWeightValue(kilo)) {
            setDisplayKgs(0);
            return setWeight(0)
        }

        const Lbs = kilo * 2.20462;

        if (Lbs % 1 === 0) {
            return setWeight(Lbs);
        }

        return setWeight(Number(Lbs.toFixed(6)));
    };

    const setLbsAmount = lbs => {
        if (!isValidWeightValue(lbs)) {
            setDisplayOnlyLbs(0);
            return setWeight(0);
        }

        setWeight(lbs);
    }

    const helperText = useMemo(() => {
        if (lbsError && customHelperText) {
            return customHelperText;
        }

        if (lbsError) {
            return "This field is required";
        }

        return "";
    }, [lbsError, customHelperText]);

    // When headerAsLabel is set, the header text becomes the field's floating label (like the
    // other form fields) instead of a separate title above the input.
    const fieldLabel = (defaultLabel) => (headerAsLabel ? header : defaultLabel);

    return (
        <>
            {header && !headerAsLabel && (
                <Grid item xs={12} md={12} lg={12} align='left' style={headerStyle} sx={headerSx}>
                    <Typography variant="subtitle1" sx={{ color: headerColor }}>
                        {required ? `${header}*` : header}
                    </Typography>
                </Grid>
            )}

            {(userWeightPreferenceLbs && !useSingleLbs) && (
                <>
                    <Grid
                        item
                        xs={columnSpan}
                        md={columnSpan}
                        lg={columnSpan}
                        align='center'
                        style={inputStyle}
                    >
                        <TextField
                            fullWidth
                            InputProps={{
                                ...(!uselabel && {
                                    endAdornment: <InputAdornment position="end">lbs.</InputAdornment>,
                                })
                            }}
                            type="number"
                            label={fieldLabel(uselabel ? "lbs." : "")}
                            onWheel={(e) => e.target.blur()}
                            variant={variant}
                            value={displayLbs}
                            required={required}
                            error={lbsError}
                            helperText={!showHelperTextContainer && helperText}
                            onChange={e => {
                                const newValue = e.target.valueAsNumber;
                                if (Number.isNaN(newValue)) {
                                    setDisplayLbs("");
                                    setLbs(0);
                                    setWeight(0);
                                    return;
                                }
                                setDisplayLbs(newValue);
                                setPounds(newValue);
                            }}
                            onBlur={e => setPounds(e.target.valueAsNumber)}
                            disabled={disabled}
                        />
                    </Grid>

                    {showConversionArrows && (
                        <Grid
                            item
                            xs={2}
                            md={2}
                            lg={2}
                            align='center'
                            sx={{ '&.MuiGrid-item': { paddingTop: '0px !important' } }}
                        >
                            <CompareArrowsIcon fontSize="large" sx={{ color: Grey2 }} />
                        </Grid>
                    )}

                    <Grid
                        item
                        xs={columnSpan}
                        md={columnSpan}
                        lg={columnSpan}
                        align='center'
                        style={inputStyle}
                    >
                        <TextField
                            fullWidth
                            variant={variant}
                            InputProps={{
                                ...(!uselabel && {
                                    endAdornment: <InputAdornment position="end">oz.</InputAdornment>,
                                })
                            }}
                            type="number"
                            label={uselabel ? "oz." : ""}
                            onWheel={(e) => e.target.blur()}
                            value={displayOzs}
                            error={lbsError}
                            onChange={e => {
                                const newValue = e.target.valueAsNumber;
                                if (Number.isNaN(newValue)) {
                                    setDisplayOzs("");
                                    setOzs(0);
                                    setWeight(parseFloat(lbs) || 0);
                                    return;
                                }
                                setDisplayOzs(newValue);
                                setOunces(newValue);
                            }}
                            onBlur={e => setOunces(e.target.valueAsNumber)}
                            disabled={disabled}
                        />
                    </Grid>

                    {
                        (showHelperTextContainer && lbsError) && (
                            <Grid item xs={12} md={12} lg={12} align='center' sx={{ marginLeft: '14px', paddingTop: '0px !important' }}>
                                <FormHelperText error>{helperText}</FormHelperText>
                            </Grid>
                        )
                    }
                </>
            )}
            {(userWeightPreferenceLbs && useSingleLbs) && (
                <>

                    <Grid
                        item
                        xs={columnSpan}
                        md={columnSpan}
                        lg={columnSpan}
                        align='center'
                        style={inputStyle}
                    >
                        <TextField
                            fullWidth
                            InputProps={{
                                ...(!uselabel && {
                                    endAdornment: <InputAdornment position="end">lbs.</InputAdornment>,
                                })
                            }}
                            type="number"
                            onWheel={(e) => e.target.blur()}
                            variant={variant}
                            value={displayOnlyLbs}
                            required={required}
                            error={lbsError}
                            label={fieldLabel(uselabel ? "lbs." : "")}
                            helperText={helperText}
                            onChange={e => {
                                const newValue = e.target.valueAsNumber;
                                if (Number.isNaN(newValue)) {
                                    setDisplayOnlyLbs("");
                                    setWeight(0);
                                    return;
                                }
                                setDisplayOnlyLbs(newValue);
                                setLbsAmount(newValue);
                            }}
                            onBlur={e => setLbsAmount(e.target.valueAsNumber)}
                            disabled={disabled}
                        />
                    </Grid>
                </>
            )}
            {userWeightPreferenceKgs && (
                <>
                    <Grid
                        item
                        xs={12}
                        md={12}
                        lg={12}
                        align='center'
                        style={inputStyle}
                    >
                        <TextField
                            fullWidth
                            InputProps={{
                                ...(!uselabel && {
                                    endAdornment: <InputAdornment position="end">Kgs.</InputAdornment>,
                                })
                            }}
                            label={fieldLabel(uselabel ? "Kgs" : "")}
                            type="number"
                            onWheel={(e) => e.target.blur()}
                            variant={variant}
                            value={displayKgs}
                            required={required}
                            error={lbsError}
                            helperText={helperText}
                            onChange={e => {
                                const newValue = e.target.valueAsNumber;
                                if (Number.isNaN(newValue)) {
                                    setDisplayKgs("");
                                    setWeight(0);
                                    return;
                                }
                                setDisplayKgs(newValue);
                                setKilo(newValue);
                            }}
                            onBlur={e => setKilo(e.target.valueAsNumber)}
                            disabled={disabled}
                        />
                    </Grid>
                </>
            )}
        </>
    );
};

WeightInput.propTypes = {
    header: PropTypes.string,
    columnSpan: PropTypes.number,
    variant: PropTypes.string,
    removePadding: PropTypes.bool,
    value: PropTypes.number.isRequired,
    onChange: PropTypes.func.isRequired,
    lbsError: PropTypes.bool,
    disabled: PropTypes.bool,
    showConversionArrows: PropTypes.bool,
    required: PropTypes.bool,
    uselabel: PropTypes.bool,
    headerSx: PropTypes.object,
    showHelperTextContainer: PropTypes.bool,
    singleLbsField: PropTypes.bool,
    headerAsLabel: PropTypes.bool,
};

WeightInput.defaultProps = {
    header: "",
    columnSpan: 4,
    variant: "outlined",
    removePadding: false,
    lbsError: false,
    disabled: false,
    showConversionArrows: false,
    required: false,
    headerColor: trakrefBlue,
    uselabel: false,
    headerSx: {},
    showHelperTextContainer: false,
    singleLbsField: false,
    headerAsLabel: false
};

export default WeightInput;
